<template>
  <div class="client-select-container dropdown d-flex">
    <a class="dropdown-toggle client-select-toggle d-flex align-items-center justify-content-between mx-2 px-2" :class="{ 'is-open': isDropdownOpen }" href="#" @click.prevent.stop="handleClientToggleClick">
      <div class="d-flex align-items-center w-100">
        <div class="d-flex align-items-center pe-2 flex-grow-1">
          <template v-if="!isDropdownOpen">
            <img v-if="selectedOrg && selectedOrg.image" :src="selectedOrg.image" class="org-logo me-3" :alt="selectedOrg?.name + ' logo'"/>
            <div v-else-if="selectedOrg" class="org-logo me-3 d-flex align-items-center justify-content-center bg-secondary text-white rounded-circle" style="width: 30px; height: 30px; font-size: 12px; font-weight: bold;">
              {{ selectedOrg.name.charAt(0).toUpperCase() }}
            </div>
            <span class="flex-grow-1 text-truncate" :class="{ 'ms-2': !selectedOrg }">{{ selectedOrg ? selectedOrg.name : 'Select Organization' }}</span>
          </template>
          <template v-else>
            <span class="toggle-search-icon"><i class="fa fa-search"></i></span>
            <input ref="toggleSearchInput" type="text" class="form-control toggle-search-input w-100" :placeholder="selectedOrg ? selectedOrg.name : 'Search by Name...'" v-model="searchKey" @input="fetchClientOptions" @keydown.enter.prevent="selectFirstVisibleClient" @click.stop aria-label="selectedOrg ? selectedOrg.name :">
          </template>
        </div>
        <div class="chevron-icon-container ms-auto">
          <span class="chevron-inner">
            <i class="fa fa-chevron-down chevron-icon m-0 p-0" :class="{ rotated: isDropdownOpen }"></i>
          </span>
        </div>
      </div>
    </a>

    <div class="dropdown-menu dropdown-menu-primary pt-0" id="client-select-dropdown-menu" style="z-index: 3000;">
      <div class="options-container">
        <span v-if="localClients.length > 0">
          <a v-for="c in localClients" :key="c.id" href="#" class="dropdown-item py-0 my-3 px-1 d-flex align-items-center" @click.prevent="selectOrg(c)">
            <img v-if="c.image" :src="c.image" class="org-logo me-3" :alt="c.name + ' logo'">
            <div v-else class="org-logo me-3 d-flex align-items-center justify-content-center bg-secondary text-white rounded-circle" style="width: 30px; height: 30px; font-size: 12px; font-weight: bold;">
              {{ c.name.charAt(0).toUpperCase() }}
            </div>
            <span class="text-truncate">{{ c.name }}</span>
          </a>
        </span>
        <span v-else class="p-2">No organizations match your search</span>
      </div>
    </div>

  
  </div>
</template>

<script>
import { useClientStore } from '../../stores/client';

export default {
  name: 'OrgSelect',
  props: {
    clients: {
      type: Array,
      required: true
    },
    client: {
      type: Object,
      default: null
    },
    user: {
      type: Object,
      default: {},
      required: true
    },
  },
  setup() {
    const clientStore = useClientStore();
    return {
      clientStore
    };
  },
  data() {
    return {
      localClients: [],
      searchKey: '',
      isDropdownOpen: false,
      agency: null,
      selectedOrg: null,
    }
  },
  mounted() {
    this.loadAgencyData();
    this.loadClientsList();
    this.setSelectedOrg();
    
    // Initialize client store with current client if available
    if (this.client && this.client.id) {
      this.clientStore.setClient(this.client);
      this.clientStore.fetchTasks();
    }
    
    this.$nextTick(() => {
      this.setupClientSelectorFocus();
      this.setupOrganizationDropdown();
      this.setupDropdownBehavior();
      // Close dropdown when clicking outside of this component
      document.addEventListener('click', this.handleDocumentClick, true);
    });
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick, true);
    if (this._dropdownInstance && typeof this._dropdownInstance.dispose === 'function') {
      this._dropdownInstance.dispose();
    }
  },
  // For Vue 3 compatibility (if used)
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick, true);
    if (this._dropdownInstance && typeof this._dropdownInstance.dispose === 'function') {
      this._dropdownInstance.dispose();
    }
  },
  computed: {
    /** When admin on an admin page, use store's full list so nav is consistent across Report Editor, Calculations/Filters, etc. */
    allEffectiveClients() {
      if (this.user?.user_type === 'admin' && this.isAdminPage() && this.clientStore.allClients?.length) {
        return this.clientStore.allClients;
      }
      return this.clients;
    }
  },
  watch: {
    clients: {
      immediate: false,
      handler() {
        this.loadClientsList();
        this.setSelectedOrg();
      }
    },
    allEffectiveClients: {
      handler() {
        this.loadClientsList();
        this.setSelectedOrg();
      },
      deep: true
    }
  },
  methods: {
    isAdminPage() {
      const path = window.location.pathname || '';
      return path === '/dashboard/admin' || path.startsWith('/admin/');
    },
    loadAgencyData() {
      // Load agency data if user is agency admin
      if (this.user && this.user.agency_id) {
        // Check if agency data is already available in the user object
        if (this.user.agency) {
          this.agency = this.user.agency;
        } else {
          // If not available, we'll need to fetch it or use the agency_id
          this.agency = {
            id: this.user.agency_id,
            name: 'Agency', // fallback name
            photo_url: null
          };
        }
      }
    },
    loadClientsList() {
      const list = [];

      // 1. If current user is system admin add the Avid Admin option
      if (this.user && this.user.user_type === 'admin') {
        list.push({
          id: 'avid_admin',
          name: 'Avid Admin',
          image: '/img/logo_png/logo_tile.png', // Ensure this image exists in public/img
          url: null,
          type: 'admin',
          text: 'Avid Admin',
          label: 'Avid Admin',
          value: 'avid_admin'
        });
      }

      // 2. If current user is agency admin add the Agency dashboard option
      else if (this.user && this.user.agency_id) {
        list.push({
          id: this.user.agency_id,
          name: this.agency ? this.agency.name : 'Agency',
          image: this.agency ? this.agency.photo_url : null,
          url: this.user.agency_id,
          type: 'agency',
          text: this.agency ? this.agency.name : 'Agency',
          label: this.agency ? this.agency.name : 'Agency',
          value: this.user.agency_id
        });
      }

      // Existing client list (use allEffectiveClients so admin nav shows full list on all admin pages)
      this.allEffectiveClients.forEach((client) => {
        const clientTemp = { ...client };
        clientTemp.category = '';
        clientTemp.text = client.name;
        clientTemp.label = client.name;
        clientTemp.value = client.id;
        clientTemp.type = 'client';
        list.push(clientTemp);
      });
      this.localClients = list;
    },
    setSelectedOrg() {
      // Determine which organization should be selected based on current page and user type
      const currentPath = window.location.pathname;
      
      // Check if we're on admin dashboard
      if (currentPath.includes('/dashboard/admin')) {
        this.selectedOrg = this.localClients.find(org => org.type === 'admin');
        return;
      }
      
      // Check if we're on agency dashboard
      if (currentPath.includes('/dashboard/agency/') || currentPath == '/') {
        this.selectedOrg = this.localClients.find(org => org.type === 'agency');
        return;
      }
      
      // If we have a client selected, use that
      if (this.client && this.client.id) {
        this.selectedOrg = this.localClients.find(org => org.type === 'client' && org.id === this.client.id);
        return;
      }
      
      // Default to first available option
      if (this.localClients.length > 0) {
        this.selectedOrg = this.localClients[0];
      }
    },
    fetchClientOptions() {
      this.loadClientsList();
      if (this.searchKey.trim() !== '') {
        const key = this.searchKey.toLowerCase();
        this.localClients = this.localClients.filter(c => (c.name || '').toLowerCase().includes(key));
      }
      this.$forceUpdate();
    },
    getOrgUrl(org) {
      if (!org) return '#';
      
      if (org.type === 'admin') {
        return '/dashboard/admin';
      }
      
      if (org.type === 'agency') {
        return '/';
      }
      
      if (org.type === 'client' && org.url) {
        return '/switch/' + org.url;
      }
      
      return '#';
    },
    selectOrg(org) {
      if (!org) return;

      this.selectedOrg = org;

      // Full page redirect (avoids 409 with Inertia when switching org context)
      const url = this.getOrgUrl(org);
      if (url && url !== '#') {
        window.location.href = url;
      }
    },
    setupDropdownBehavior() {
      const toggle = this.$el.querySelector('.client-select-toggle');
      const menu = this.$el.querySelector('#client-select-dropdown-menu');
      if (!toggle || !menu) return;

      // Initialize Bootstrap dropdown instance
      let dropdownInstance = null;
      const dropdownOptions = {
        autoClose: true,
        /**
         * Popper re-computes position on scroll of any "overflow ancestor" (including the dropdown
         * menu itself since it has `overflow-y: auto`). That reflow is what causes the visible
         * jump while scrolling. Disable scroll listeners so the menu stays locked to the toggle
         * position while you scroll inside the menu.
         */
        popperConfig: (defaultBsPopperConfig) => {
          const modifiers = defaultBsPopperConfig?.modifiers ?? [];

          return {
            ...defaultBsPopperConfig,
            modifiers: [
              ...modifiers.filter((m) => m?.name !== 'eventListeners'),
              {
                name: 'eventListeners',
                enabled: true,
                options: { scroll: false, resize: true },
              },
            ],
          };
        },
      };
      if (window && window.bootstrap && window.bootstrap.Dropdown) {
        const existing = window.bootstrap.Dropdown.getInstance(toggle);
        if (existing && typeof existing.dispose === 'function') {
          existing.dispose();
        }
        dropdownInstance = new window.bootstrap.Dropdown(toggle, dropdownOptions);
      } else if (typeof bootstrap !== 'undefined' && bootstrap.Dropdown) {
        const existing = bootstrap.Dropdown.getInstance(toggle);
        if (existing && typeof existing.dispose === 'function') {
          existing.dispose();
        }
        dropdownInstance = new bootstrap.Dropdown(toggle, dropdownOptions);
      }

      // Store instance for later use
      this._dropdownInstance = dropdownInstance;

      // Listen to Bootstrap events
      toggle.addEventListener('shown.bs.dropdown', () => {
        this.isDropdownOpen = true;
        this.$nextTick(() => this.focusToggleSearch());
      });
      toggle.addEventListener('hidden.bs.dropdown', () => {
        this.isDropdownOpen = false;
        this.searchKey = '';
      });
    },
    handleClientToggleClick() {
      if (this._dropdownInstance && typeof this._dropdownInstance.toggle === 'function') {
        this._dropdownInstance.toggle();
      }
    },
    focusToggleSearch() {
      const input = this.$refs.toggleSearchInput;
      if (input && typeof input.focus === 'function') {
        input.focus();
      }
    },
    focusClientSearch() {
      const searchInput = document.querySelector('#client-select-dropdown-menu .search-box');
      if (searchInput) {
        searchInput.focus();
      }
    },
    handleDocumentClick(event) {
      if (!this.isDropdownOpen) return;
      // If click is outside this component, hide the dropdown
      if (this.$el && !this.$el.contains(event.target)) {
        if (this._dropdownInstance && typeof this._dropdownInstance.hide === 'function') {
          this._dropdownInstance.hide();
        }
        this.isDropdownOpen = false;
        this.searchKey = '';
      }
    },
    selectFirstVisibleClient() {
      if (this.localClients && this.localClients.length > 0) {
        this.selectOrg(this.localClients[0]);
      }
    },
    setupClientSelectorFocus() {
      document.addEventListener('shown.bs.dropdown', (event) => {
        if (event.target && event.target.id === 'client-select-dropdown-menu') {
          this.focusToggleSearch();
        }
      });
    },
    setupOrganizationDropdown() {
      const organizationDropdown = document.getElementById('client-select-dropdown-menu');
      if (!organizationDropdown) return;
      organizationDropdown.addEventListener('click', (event) => {
        event.stopPropagation();
      });
      const searchInput = organizationDropdown.querySelector('.search-box');
      if (searchInput) {
        searchInput.addEventListener('click', (event) => {
          event.stopPropagation();
        });
      }
    }
  }
}
</script>
